// --- React
import React, { useCallback } from 'react';
// --- UI Components
import { Col, Row } from 'antd';
import { InputLabel } from '../../../InputLabel/InputLabel';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
import Button from '@dtci/spellbook/Button';
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Redux
import { useSelector } from 'react-redux';
import { useCommentsActions } from '../../../../store/comments/useComments';
// --- API
import { datastageApi } from '../../../../api';
// --- Utilities
import { commentTypes } from '../../../../utils/commentType';
import { filterCriteria } from '../../../../utils/filterCriteria';
import { saveAs } from 'file-saver';
// --- Hooks
import { useAgGridApi } from '../../../AGGrid/hooks';
import { useLoading } from '../../../../utils/hooks/useLoading';
// --- Styles
import './styles.scss';
import styles from '../../../AGGrid/details-table.module.scss';
import { usePermissions } from '../../../../utils/permissions';

// --- Config
import * as columnDefs from '../../../AGGrid/config/columns-def';
import { DetailsRow } from '../../../AGGrid/details-row/details-row';
import {useSharedSectionsGridUtils} from "../../../../pages/SharedSections/components/grid/utils/gridUtils";
import {useCommentsGridUtils} from "../utils/gridUtils";

/**
 * CommentTable Component
 * Displays the comments in a table with pagination.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.agGridApi - API object for interacting with the AG Grid.
 * @param {Array} props.comments - List of comments to display in the table.
 * @returns {JSX.Element} - Rendered CommentTable component.
 */
const CommentTable = ({ agGridApi, comments, onRowSelection, onOpenAttachmentsModal }) => {

    const { commentProduct, filterForm, commentRange} = useSelector((s) => s.comments);
    const {updateComments} = useCommentsActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();

    const onRowClicked = useCallback(
        (event) => {
            onRowSelection(event.data);
        },
        [onRowSelection],
    );

    const {
        setSort,
        columns
    } = useCommentsGridUtils({
        datastageApi,
        filterForm,
        commentRange,
        commentProduct,
        updateComments,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => () => onOpenAttachmentsModal(data),
            ]
        }
    });

    return (
        <div className="ag-table-wrapper">
            <div className="ag-table-container">
                <DataGrid
                    className={styles.dataGrid}
                    agGrid={{
                        ref: agGridApi.AgGridApiRef,
                        rowData: comments,
                        headerHeight: 45,
                        domLayout: 'normal',
                        onRowClicked,
                        detailCellRenderer: (params) => <DetailsRow dataType="comments" data={params.data} />,
                        detailRowAutoHeight: true,
                        masterDetail: true,
                    }}
                    bordered="none"
                    columnDefs={columns}
                    sortable={false}
                    striped
                />
            </div>
        </div>
    );
};

/**
 * ViewCommentsForm Component
 *
 * This component is responsible for rendering the form used to view comments.
 * It includes a filter form for searching comments by text and type,
 * as well as a button to create new comments.
 * It also handles the logic for filtering, resetting filters,
 * and pagination of the comments list.
 *
 * @component
 * @returns {JSX.Element} The rendered ViewCommentsForm component.
 */
export function ViewCommentsForm() {
    const agGridApi = useAgGridApi();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { comments, commentProduct, filterForm, commentRange, commentSort} = useSelector((s) => s.comments);
    const { updateComments, updateCommentsFilterForm } = useCommentsActions();
    const { isLegalAdmin, isLegalUser, isProductCommentsAdmin } = usePermissions();

    // Function to handle the close action of the comments modal
    const handleRowSelection = (data) => {
        if (!data) return;
        updateComments({
            isCreateModalOpen: false,
            isEditModalOpen: true,
            comment: data,
        });
    };

    // Function to handle the close action of the create comment modal
    const onOpenCreateCommentModal = () => {
        updateComments({
            comment: [],
            isCreateModalOpen: true,
            isEditModalOpen: false,
        });
    };

    // Function to handle the close action of the attachments modal
    const onOpenAttachmentsModal = (data) => {
        if (!data) return;
        updateComments({
            isAttachmentsModalOpen: true,
            isCreate: false,
            comment: data,
        });
    };

    // Function to handle the filter action of the comments
    const handleFilter = async () => {
        setLoadingTrue();
        const data = await datastageApi.comments(
            filterCriteria(filterForm, commentProduct, 'comments'),
            commentRange,
            commentSort,
        );

        updateComments({
            comments: data?.content,
            totalPages: data?.totalPages,
            totalItems: data?.totalElements,
            currentPage: data?.pageable.pageNumber,
        });
        setLoadingFalse();
    };

    // Function to handle the reset action of the comments filter
    const handleResetFilter = async () => {
        setLoadingTrue();

        const resetFilter = {
            productId: filterForm?.productId,
            clearanceMemoTitleId: filterForm.clearanceMemoTitleId,
        };

        const data = await datastageApi.comments(
            filterCriteria(resetFilter, commentProduct, 'comments'),
            [0, 9],
            commentSort,
        );

        updateComments({
            isCommentModalOpen: true,
            comments: data?.content,
            totalPages: data?.totalPages,
            currentPage: data?.pageable.pageNumber,
            totalItems: data?.pageable.totalElements,
        });

        updateCommentsFilterForm({
            key: 'filterForm',
            data: { commentType: '' },
        });

        updateCommentsFilterForm({
            key: 'filterForm',
            data: { bodyText: '' },
        });

        setLoadingFalse();
    };

    const handleExport = async () => {
        const {clearanceMemoTitleId, productId} = commentProduct;

        setLoadingTrue();
        const res = await datastageApi.exportComments(clearanceMemoTitleId, productId)
        saveAs(res.data, 'comments.pdf');

        setLoadingFalse();

    };

    return (
        <>
         <Row className="comments-filter-form">
              <Col span={16} className="filters-container">
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'nowrap' }}>
                      <div style={{ flex: 2 }}>
                          <InputLabel colProps={{ span: 24 }} label="Filter By Comment Text">
                              <Input
                                  value={filterForm.bodyText}
                                  onChange={(e) =>
                                      updateCommentsFilterForm({
                                          key: 'filterForm',
                                          data: { bodyText: e.target.value },
                                      })
                                  }
                                  onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                          handleFilter();
                                      }
                                  }}
                                  id="bodyText"
                                  name="bodyText"
                                  placeholder="Enter"
                              />
                          </InputLabel>
                      </div>
                      <div style={{ flex: 1 }}>
                          <InputLabel colProps={{ span: 24 }} label="Filter By Comment Type">
                              <Select
                                  onChange={(value) => {
                                      updateCommentsFilterForm({
                                          key: 'filterForm',
                                          data: { commentType: value },
                                      });
                                  }}
                                  name="commentType"
                                  options={commentTypes}
                                  isClearable
                                  isSearchable
                                  value={filterForm.commentType}
                              />
                          </InputLabel>
                      </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <Button
                          className="filter-button"
                          sizeType="small"
                          styleType="promoted"
                          onClick={handleFilter}
                      >
                          Filter
                      </Button>
                      <Button
                          className="reset-button"
                          sizeType="small"
                          styleType="standard"
                          onClick={handleResetFilter}
                      >
                          Reset
                      </Button>
                  </div>
              </Col>
              <Col span={8} className="buttons-container">
                  {(isLegalAdmin || isLegalUser || isProductCommentsAdmin) && (
                      <Button
                          iconType="fa-regular fa-file-pdf"
                          className="filter-button"
                          sizeType="small"
                          styleType="link"
                          onClick={handleExport}
                      >
                          Export to PDF
                      </Button>
                  )}
                  {(isLegalAdmin || isProductCommentsAdmin) && (
                      <Button
                          iconType="fa-regular fa-plus"
                          className="filter-button"
                          sizeType="small"
                          styleType="link"
                          onClick={onOpenCreateCommentModal}
                      >
                          Create
                      </Button>
                  )}
              </Col>
          </Row>
            <CommentTable
                agGridApi={agGridApi}
                comments={comments}
                onRowSelection={handleRowSelection}
                onOpenAttachmentsModal={onOpenAttachmentsModal}
            />
        </>
    );
}
